import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { TenantBranding } from '../_model/tenant-branding.model';
import { TENANT_MAP } from '../../core/tenant.config';
import { environment } from '../../../environments/environment';

export interface Tenant {
	tenantId: number;
	name: string;
	landingPage: string | null;
}

const TENANT_LOCAL_LOGOS: Record<number, string> = {
	1: '/assets/img/imatra/logo.svg',
	2: '/assets/img/aic/logo.png',
};

const DEFAULT_LOGO = '/assets/images/PIB Italia Broker_logo.png';

const TENANT_STEP_IMAGES: Record<number, { primo: string; altri: string }> = {
	1: { primo: '/assets/img/imatra/mountain bike-rafiki.svg', altri: '/assets/img/imatra/bike travellers-cuate.svg' },
	2: { primo: '/assets/img/aic/2.png', altri: '/assets/img/aic/1.png' },
};

const DEFAULT_STEP_IMAGES = {
	primo: '/assets/images/Soccer-amico.svg',
	altri: '/assets/images/Football Goal-amico.svg',
};

const TENANT_ERROR_IMAGES: Record<number, string> = {
	1: '/assets/img/imatra/Bike trial-rafiki.svg',
	2: '/assets/img/aic/3.png',
};

const DEFAULT_ERROR_IMAGE = '/assets/images/Missed chances-amico 2.svg';

const TENANT_SUCCESS_IMAGES: Record<number, string> = {
	1: '/assets/img/imatra/Bicycle workshop-cuate (1).svg',
	2: '/assets/img/aic/4.png',
};

const DEFAULT_SUCCESS_IMAGE = '/assets/images/High five-amico.svg';

@Injectable({ providedIn: 'root' })
export class ThemeService {
	private tenantId = Number(sessionStorage.getItem('tenantId') ?? 1);
	tenants: Tenant[] = [];
	logoUrl = '';

	constructor(private http: HttpClient) {}

	setTenant(tenantId: number): void {
		this.tenantId = tenantId;
		sessionStorage.setItem('tenantId', String(tenantId));
	}

	resetToDefault(): void {
		this.tenantId = 0;
		this.logoUrl = '';
		const root = document.documentElement;
		[
			'--color-primary',
			'--color-secondary',
			'--color-dark',
			'--color-grey',
			'--color-bg',
			'--color-light',
			'--color-info',
			'--color-highlight',
			'--color-alert',
			'--font-family',
		].forEach((v) => root.style.removeProperty(v));
	}

	loadTenants(): Promise<void> {
		return firstValueFrom(
			this.http.post<{ esito: string; descrizione: string; result: Tenant[] }>(environment.apiUrl + 'common/tenants?noBlock', {
				id: null,
				language: 'it',
			}),
		)
			.then((response) => {
				this.tenants = response?.result ?? [];
				console.log('[ThemeService] tenants:', this.tenants);

				// Verifica che la mappatura URL → tenantId sia corretta
				Object.entries(TENANT_MAP).forEach(([path, tenantId]) => {
					const match = this.tenants.find((t) => t.tenantId === tenantId);
					console.log(`[ThemeService] /${path} → tenantId ${tenantId} → ${match?.name ?? 'NON TROVATO'}`);
				});
			})
			.catch((err) => {
				console.warn('[ThemeService] loadTenants error:', err);
			});
	}

	init(): Promise<void> {
		return firstValueFrom(
			this.http.post<{ esito: string; descrizione: string; result: TenantBranding[] }>(
				environment.apiUrl + 'common/tenant-branding?noBlock',
				{ id: this.tenantId, language: 'it' },
			),
		)
			.then((response) => {
				const branding = response?.result?.[0];
				if (branding) {
					this.applyBranding(branding);
				}
			})
			.catch(() => {
				// Fallback silenzioso: i CSS vars di default in _variables.scss mantengono i colori PIB
			});
	}

	getTenantId(): number {
		return this.tenantId;
	}

	getLogoUrl(): string {
		return this.logoUrl || TENANT_LOCAL_LOGOS[this.tenantId] || DEFAULT_LOGO;
	}

	getLandingUrl(): string {
		const map: Record<number, string> = {
			1: '/landing-page-imatra',
			2: '/landing-page-aic',
		};
		return map[this.tenantId] ?? '/';
	}

	getErrorImage(): string {
		return TENANT_ERROR_IMAGES[this.tenantId] ?? DEFAULT_ERROR_IMAGE;
	}

	getSuccessImage(): string {
		return TENANT_SUCCESS_IMAGES[this.tenantId] ?? DEFAULT_SUCCESS_IMAGE;
	}

	getStepImage(isPrimoStep: boolean): string {
		const images = TENANT_STEP_IMAGES[this.tenantId] ?? DEFAULT_STEP_IMAGES;
		return isPrimoStep ? images.primo : images.altri;
	}

	private applyBranding(branding: TenantBranding): void {
		const root = document.documentElement;

		if (branding.logoUrl) this.logoUrl = branding.logoUrl;

		if (branding.primaryColor) root.style.setProperty('--color-primary', branding.primaryColor);
		if (branding.secondaryColor) root.style.setProperty('--color-secondary', branding.secondaryColor);
		if (branding.dark) root.style.setProperty('--color-dark', branding.dark);
		if (branding.grey) root.style.setProperty('--color-grey', branding.grey);
		if (branding.greyLight) root.style.setProperty('--color-bg', branding.greyLight);
		if (branding.light) root.style.setProperty('--color-light', branding.light);
		if (branding.info) root.style.setProperty('--color-info', branding.info);
		if (branding.highlight) root.style.setProperty('--color-highlight', branding.highlight);
		if (branding.alert) root.style.setProperty('--color-alert', branding.alert);
		if (branding.fontFamily) root.style.setProperty('--font-family', branding.fontFamily);

		if (branding.faviconUrl) {
			const favicon = document.querySelector<HTMLLinkElement>('link[rel="icon"]');
			if (favicon) favicon.href = branding.faviconUrl;
		}

		if (branding.customCssUrl) {
			const link = document.createElement('link');
			link.rel = 'stylesheet';
			link.href = branding.customCssUrl;
			document.head.appendChild(link);
		}
	}
}
